<template>
  <nav class="navbar navbar-expand-md navbar-dark bg-dark">
    <div class="container">
      <router-link v-if="authChecked"
                   :to="{ name: allowMultipleExperiments ? 'app.dashboard.index' : 'app.current_experiment' }"
                   class="navbar-brand">
        <img v-if="navLogoUrl" class="h-12" :src="navLogoUrl" :alt="appName" />
        <span v-else v-text="appName"></span>
      </router-link>

      <a href="/" v-else
         class="navbar-brand">
        <img v-if="navLogoUrl" class="h-12" :src="navLogoUrl" :alt="appName" />
        <span v-else v-text="appName"></span>
      </a>

      <button class="navbar-toggler"
              type="button"
              data-toggle="collapse"
              data-target="#navbarSupportedContent"
              aria-controls="navbarSupportedContent"
              aria-expanded="false"
              :aria-label="$t('messages.toggle_navigation')">
        <span class="navbar-toggler-icon"></span>
      </button>

      <div class="collapse navbar-collapse" id="navbarSupportedContent">
        <!-- Left Side Of Navbar -->
        <ul class="navbar-nav mr-auto lg:ml-32">
          <li class="nav-item dropdown mr-4">
            <a id="aboutDropdown"
               class="nav-link text-white font-bold"
               href="#"
               role="button"
               data-toggle="dropdown"
               aria-haspopup="true"
               aria-expanded="false">
              <span v-text="$t('messages.about')"></span>
              <fa icon="caret-down" class="ml-3"/>
            </a>
            <div class="dropdown-menu" aria-labelledby="aboutDropdown">
              <router-link
                class="dropdown-item"
                :to="{ name: 'app.pages', params: {content: 'about'}}"
                target="_blank"
              >
                {{ appName }}
              </router-link>

              <router-link
                class="dropdown-item"
                :to="{ name: 'app.pages', params: {content: 'terms-and-privacy'}}"
                target="_blank"
              >
                {{ $t('messages.terms_and_privacy') }}
              </router-link>
            </div>
          </li>

          <li v-if="experiment && inExperiment && experimentIsOnGoing" class="nav-item dropdown">
            <a id="experimentDropdown"
               class="nav-link text-white font-bold"
               href="#"
               role="button"
               data-toggle="dropdown"
               aria-haspopup="true"
               aria-expanded="false" v-text="$tc('messages.experiment', 1)">
            </a>
            <div class="dropdown-menu" aria-labelledby="experimentDropdown">
              <router-link
                v-for="content in experimentContents"
                :key="`experiment_content_link${content.id}`"
                class="dropdown-item"
                target="_blank"
                :to="{ name: 'app.experiments.contents', params: {id: experiment.id, content: content.slug}}"
              >
                {{ content.title }}
              </router-link>
              <a class="dropdown-item"
                 href="#"
                 v-text="$t('messages.quit_early')"
                 @click.prevent="quit"
              >
              </a>
            </div>
          </li>
        </ul>

        <!-- Right Side Of Navbar -->
        <ul class="navbar-nav ml-auto">
          <!-- Authenticated -->
          <li v-if="authChecked" class="nav-item dropdown">
            <a class="nav-link dropdown-toggle text-white flex items-center"
               href="#"
               role="button"
               data-toggle="dropdown"
               aria-haspopup="true"
               aria-expanded="false">
              <!--<img :src="authUser.avatar" class="w-10 rounded-circle profile-photo mr-3"/>-->
              <span v-text="authUser.username"></span>
            </a>
            <div class="dropdown-menu">
              <template v-if="$permission.isAllowed('accessWithAny', ['access_bo'])">
                <a href="/back-office" class="dropdown-item pl-3">
                  <fa icon="tachometer-alt" fixed-width/>
                  {{ $t('messages.back_office') }}
                </a>

                <div class="dropdown-divider"></div>
              </template>

              <a href="#" class="dropdown-item pl-3" @click.prevent="logout">
                <fa icon="sign-out-alt" fixed-width/>
                {{ $t('messages.logout') }}
              </a>
              <form id="logout-form" :action="logoutAction" method="POST" style="display: none;">
                <input type="hidden" name="_token" v-model="csrfToken"/>
              </form>
            </div>
          </li>

          <!-- Guest -->
          <template v-else>
            <li class="nav-item">
              <router-link :to="{ name: 'app.auth.login' }"
                           class="nav-link"
                           active-class="active"
                           v-text="$t('messages.login')"/>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script>
import {route} from '@/http/ziggy';
import includes from 'lodash/includes';
import {mapState, mapActions, mapGetters} from 'vuex';
import permissionsPerimeter from '@/perimeters/permissions';

export default {
  data() {
    return {
      appName: window.config.appName,
      navLogoUrl: window.config.navLogoUrl,
      csrfToken: window.csrf_token,
    }
  },

  computed: {
    ...mapState('auth', {
      authChecked: state => state.checked,
      authUser: state => state.user
    }),
    ...mapState('civium', {
      experiment: state => state.experiment,
      allowMultipleExperiments: state => state.allowMultipleExperiments,
    }),
    ...mapGetters('civium', [
      'experimentIsOnGoing'
    ]),

    /**
     * Return experiment contents.
     */
    experimentContents() {
      return this.experiment.contents
    },

    /**
     * Will check if we are in an experiment.
     */
    inExperiment() {
      return includes(this.$route.path, 'experiments');
    },

    logoutAction() {
      return route('auth.logout.process');
    }
  },

  perimeters: [
    permissionsPerimeter
  ],

  methods: {
    ...mapActions('civium', [
      'quitExperiment'
    ]),

    /**
     * Show a quit early confirmation.
     */
    experimentQuitConfirmation({title, text}) {
      return this.$swal({
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('messages.yes'),
        cancelButtonText: this.$t('messages.no'),
        allowOutsideClick: false,
      });
    },

    /**
     * Ask use if they want to quit experiment.
     */
    quit() {
      this
        .experimentQuitConfirmation({
          title: this.$t('messages.are_you_sure'),
          text: this.$t('messages.quit_early_warning'),
        })
        .then((result) => {
          if (result.value) {
            this.quitExperiment();
          }
        });
    },

    /**
     * Logout from the App.
     */
    logout() {
      if (this.inExperiment && this.experimentIsOnGoing) {
        this
          .experimentQuitConfirmation({
            title: this.$t('messages.are_you_sure'),
            text: this.$t('messages.quit_early_warning_logout'),
          })
          .then(async (result) => {
            if (result.value) {
              await this.quitExperiment();
              document.getElementById('logout-form').submit();
            }
          });
      } else {
        document.getElementById('logout-form').submit();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/bootstrap';
</style>
